import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { CheckCircle, AlertCircle, CreditCard } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Load Razorpay script
const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

export const PaymentModal = ({ isOpen, onClose, applicationId, paymentOption, applicantData }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [fetchedApplicantData, setFetchedApplicantData] = useState(null);

  // Fetch applicant data from backend when modal opens
  React.useEffect(() => {
    const fetchApplicantData = async () => {
      if (isOpen && applicationId && !fetchedApplicantData) {
        try {
          console.log('Fetching application data for:', applicationId);
          const response = await fetch(`${API}/applications/${applicationId}`);
          if (response.ok) {
            const data = await response.json();
            console.log('Fetched applicant data:', data);
            setFetchedApplicantData({
              fullName: data.fullName,
              email: data.email,
              phone: data.phone
            });
          } else {
            console.error('Failed to fetch application data');
          }
        } catch (error) {
          console.error('Error fetching application:', error);
        }
      }
    };

    fetchApplicantData();
  }, [isOpen, applicationId, fetchedApplicantData]);

  const handlePayment = async () => {
    setIsProcessing(true);

    // Use fetched data if available, otherwise use passed data
    const dataToUse = fetchedApplicantData || applicantData;
    
    console.log('Payment modal - applicantData from props:', applicantData);
    console.log('Payment modal - fetchedApplicantData:', fetchedApplicantData);
    console.log('Payment modal - using data:', dataToUse);

    try {
      // Load Razorpay script
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        toast.error('Failed to load payment gateway. Please try again.');
        setIsProcessing(false);
        return;
      }

      // Create payment order
      const orderResponse = await fetch(`${API}/payments/create-order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: paymentOption.amount,
          applicationId: applicationId,
          paymentType: paymentOption.paymentType
        })
      });

      if (!orderResponse.ok) {
        throw new Error('Failed to create payment order');
      }

      const orderData = await orderResponse.json();

      // Format phone number for Razorpay (remove country code, keep only 10 digits)
      let phoneNumber = dataToUse?.phone || '';
      console.log('Original phone from data:', phoneNumber);
      
      // Remove +91, +, spaces, and hyphens
      phoneNumber = phoneNumber.replace(/[\+\s\-]/g, '');
      console.log('After removing special chars:', phoneNumber);
      
      // If it starts with 91 and is 12 digits, remove the 91
      if (phoneNumber.startsWith('91') && phoneNumber.length === 12) {
        phoneNumber = phoneNumber.substring(2);
        console.log('After removing 91 prefix:', phoneNumber);
      }
      // Keep only last 10 digits
      phoneNumber = phoneNumber.slice(-10);
      
      console.log('Final formatted phone for Razorpay:', phoneNumber);
      console.log('Razorpay Prefill Data:', {
        name: dataToUse?.fullName,
        email: dataToUse?.email,
        contact: phoneNumber
      });

      // Razorpay options
      const options = {
        key: orderData.keyId,
        amount: orderData.amount,
        currency: orderData.currency,
        name: 'Fit n Shine Wellness Pvt. Ltd.',
        description: `Wellness Coach Academy - ${paymentOption.name}`,
        order_id: orderData.orderId,
        handler: async function (response) {
          try {
            // Verify payment
            const verifyResponse = await fetch(`${API}/payments/verify`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                applicationId: applicationId,
                paymentType: paymentOption.paymentType
              })
            });

            if (!verifyResponse.ok) {
              throw new Error('Payment verification failed');
            }

            toast.success('Payment successful! Your enrollment is confirmed.', { duration: 6000 });
            setIsProcessing(false);
            onClose(true); // Close with success flag
            
          } catch (error) {
            console.error('Payment verification error:', error);
            toast.error('Payment verification failed. Please contact support.');
            setIsProcessing(false);
          }
        },
        prefill: {
          name: dataToUse?.fullName || '',
          email: dataToUse?.email || '',
          contact: phoneNumber
        },
        theme: {
          color: '#2D8B51'
        },
        readonly: {
          email: false,
          contact: false
        },
        modal: {
          ondismiss: function() {
            // Restore pointer events on our modals
            const modalBackdrops = document.querySelectorAll('[role="dialog"]');
            modalBackdrops.forEach(backdrop => {
              if (backdrop.style) {
                backdrop.style.pointerEvents = 'auto';
              }
            });
            
            setIsProcessing(false);
            toast.error('Payment cancelled');
          },
          escape: true,
          backdropclose: true
        }
      };

      const razorpay = new window.Razorpay(options);
      
      // Ensure Razorpay modal has proper z-index and is clickable
      // Close our payment modal to prevent conflicts
      const modalBackdrops = document.querySelectorAll('[role="dialog"]');
      modalBackdrops.forEach(backdrop => {
        if (backdrop.style) {
          backdrop.style.pointerEvents = 'none';
        }
      });
      
      razorpay.open();
      
      // Re-enable pointer events after Razorpay opens
      setTimeout(() => {
        const razorpayFrame = document.querySelector('iframe[name="razorpay-checkout-frame"]');
        if (razorpayFrame && razorpayFrame.parentElement) {
          razorpayFrame.parentElement.style.zIndex = '10000000';
          razorpayFrame.parentElement.style.pointerEvents = 'auto';
        }
      }, 500);

    } catch (error) {
      console.error('Payment error:', error);
      toast.error('Failed to initiate payment. Please try again.');
      setIsProcessing(false);
    }
  };

  if (!paymentOption) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !isProcessing && onClose(open)}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold flex items-center gap-2">
            <CreditCard className="w-6 h-6 text-green-600" />
            Complete Payment
          </DialogTitle>
          <DialogDescription>
            Secure your seat in the Wellness Coach Academy
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Payment Summary */}
          <div className="bg-gray-50 rounded-lg p-6">
            <h3 className="font-semibold text-lg mb-4">{paymentOption.name}</h3>
            
            {paymentOption.paymentType === 'installment' ? (
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">Installment Amount:</span>
                  <span className="font-bold text-2xl text-green-600">{paymentOption.price}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Total (4 installments):</span>
                  <span className="font-semibold">{paymentOption.totalPrice}</span>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  You'll pay the first installment now. Remaining installments will be charged automatically.
                </p>
              </div>
            ) : paymentOption.paymentType === 'scholarship' ? (
              <div className="space-y-3">
                <div className="flex justify-between items-baseline">
                  <span className="text-gray-600">Amount to Pay:</span>
                  <span className="font-bold text-3xl text-green-600">{paymentOption.price}</span>
                </div>
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                  <p className="text-sm text-purple-900 font-semibold mb-1">
                    🎓 100% Refund Eligible
                  </p>
                  <p className="text-xs text-purple-700">
                    Get full ₹60,000 refund at course completion if you score 85%+ in theory and practicals. 
                    Pay now, earn it back through performance!
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex justify-between items-baseline">
                <span className="text-gray-600">Total Amount:</span>
                <span className="font-bold text-3xl text-green-600">{paymentOption.price}</span>
              </div>
            )}
          </div>

          {/* Security Badge */}
          <div className="flex items-start gap-3 text-sm text-gray-600 bg-blue-50 p-4 rounded-lg">
            <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-blue-900 mb-1">Secure Payment</p>
              <p>Your payment is processed securely through Razorpay with 256-bit encryption.</p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => onClose(false)}
              disabled={isProcessing}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={handlePayment}
              disabled={isProcessing}
              className="flex-1 btn-primary-green btn-transition"
            >
              {isProcessing ? 'Processing...' : `Pay ${paymentOption.price}`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
